import React, { useState, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Progress } from './ui/progress';
import { Download, Upload, AlertTriangle, CheckCircle, FileJson, RefreshCw, Database } from 'lucide-react';
import { useAppData } from '../hooks/useAppData';
import * as FileSaver from 'file-saver';

export function BackupManager() {
  const {
    exportData,
    importData,
    lots,
    expenses,
    sellers,
    usuarios,
    config,
    lotGroups,
    lotHistory,
    markedPayments,
    comisiones
  } = useAppData();

  const [importMode, setImportMode] = useState<'merge' | 'overwrite'>('merge');
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState<string | null>(null);
  const [lastBackupDate, setLastBackupDate] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleExport = async () => {
    try {
      setIsExporting(true);
      // Simular tiempo de proceso
      await new Promise(resolve => setTimeout(resolve, 800));

      const data = {
        version: '1.0',
        timestamp: new Date().toISOString(),
        environment: process.env.NODE_ENV,
        stats: {
          lots: lots.length,
          expenses: expenses.length,
          sellers: sellers.length,
          users: usuarios.length
        },
        data: {
          lots,
          expenses,
          sellers,
          usuarios,
          config,
          lotGroups,
          lotHistory,
          markedPayments,
          comisiones
        }
      };

      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const fileName = `bimobi-backup-${new Date().toISOString().split('T')[0]}.json`;
      FileSaver.saveAs(blob, fileName);

      setLastBackupDate(new Date().toLocaleString());
      setImportStatus('Copia de seguridad descargada exitosamente.');
      setTimeout(() => setImportStatus(null), 3000);
    } catch (error) {
      console.error('Error exportando datos:', error);
      setImportStatus('Error al generar la copia de seguridad.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        setIsImporting(true);
        setImportProgress(10);
        setImportStatus('Analizando archivo...');

        const content = event.target?.result as string;
        const backupData = JSON.parse(content);

        if (!backupData.data || !backupData.timestamp) {
          throw new Error('Formato de archivo inválido');
        }

        setImportProgress(30);
        setImportStatus(`Archivo válido del ${new Date(backupData.timestamp).toLocaleDateString()}. Iniciando importación...`);

        // Simular proceso de importación
        await new Promise(resolve => setTimeout(resolve, 500));
        setImportProgress(60);

        // Ejecutar importación real
        await importData(backupData.data, importMode === 'merge');

        setImportProgress(100);
        setImportStatus('¡Importación completada exitosamente!');
        setTimeout(() => {
          setImportStatus(null);
          setImportProgress(0);
        }, 3000);

      } catch (error) {
        console.error('Error importando datos:', error);
        setImportStatus('Error: El archivo está dañado o tiene un formato incorrecto.');
        setImportProgress(0);
      } finally {
        setIsImporting(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Exportar Datos */}
      <Card className="bg-card border-border shadow-none rounded-xl overflow-hidden">
        <CardHeader className="border-b border-border/50 pb-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Download className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg font-semibold text-foreground">Exportar Datos</CardTitle>
              <CardDescription className="text-muted-foreground">Genera una copia de seguridad completa del sistema</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6 space-y-6">
          <Alert className="bg-accent/10 border-accent/20 text-foreground">
            <Database className="h-4 w-4 text-primary" />
            <AlertTitle className="text-primary font-medium ml-2">Información incluida</AlertTitle>
            <AlertDescription className="mt-2 text-muted-foreground text-sm">
              <ul className="list-disc list-inside space-y-1 ml-1">
                <li>Catálogo de lotes y estados</li>
                <li>Historial de pagos y movimientos</li>
                <li>Base de datos de vendedores y usuarios</li>
                <li>Configuraciones del sistema</li>
              </ul>
            </AlertDescription>
          </Alert>

          <div className="flex flex-col gap-4">
            {lastBackupDate && (
              <div className="flex items-center gap-2 text-sm bg-muted/50 border border-border rounded-lg p-3">
                <CheckCircle className="h-4 w-4 text-emerald-500" />
                <span className="text-muted-foreground">Última copia: <span className="font-medium text-foreground">{lastBackupDate}</span></span>
              </div>
            )}

            <Button
              onClick={handleExport}
              disabled={isExporting}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 text-base shadow-sm"
            >
              {isExporting ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Generando respaldo...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Descargar Copia de Seguridad
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Importar Datos */}
      <Card className="bg-card border-border shadow-none rounded-xl overflow-hidden">
        <CardHeader className="border-b border-border/50 pb-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-orange-500/10 rounded-lg">
              <Upload className="h-5 w-5 text-orange-500" />
            </div>
            <div>
              <CardTitle className="text-lg font-semibold text-foreground">Importar Datos</CardTitle>
              <CardDescription className="text-muted-foreground">Restaura información desde un archivo de respaldo</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6 space-y-6">
          <Alert variant="destructive" className="bg-destructive/10 border-destructive/20 text-destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle className="font-medium ml-2">Precaución</AlertTitle>
            <AlertDescription className="mt-2 text-destructive/80 text-sm">
              Esta acción modificará la base de datos actual. Asegúrate de tener un respaldo reciente antes de continuar.
            </AlertDescription>
          </Alert>

          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">Modo de importación</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setImportMode('merge')}
                className={`p-3 rounded-lg border text-sm font-medium transition-all flex flex-col items-center gap-2 ${importMode === 'merge'
                  ? 'border-primary/50 bg-primary/10 text-primary'
                  : 'border-border bg-secondary hover:bg-accent text-muted-foreground'
                  }`}
              >
                <RefreshCw className="h-4 w-4" />
                <span>Combinar (Merge)</span>
              </button>
              <button
                onClick={() => setImportMode('overwrite')}
                className={`p-3 rounded-lg border text-sm font-medium transition-all flex flex-col items-center gap-2 ${importMode === 'overwrite'
                  ? 'border-destructive/50 bg-destructive/10 text-destructive'
                  : 'border-border bg-secondary hover:bg-accent text-muted-foreground'
                  }`}
              >
                <AlertTriangle className="h-4 w-4" />
                <span>Sobrescribir</span>
              </button>
            </div>
            <p className="text-xs text-muted-foreground px-1">
              {importMode === 'merge'
                ? 'Agrega datos nuevos y actualiza existentes. No elimina información.'
                : 'Reemplaza COMPLETAMENTE la base de datos actual con el archivo.'}
            </p>
          </div>

          <div className="space-y-4">
            {isImporting && (
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Progreso</span>
                  <span>{importProgress}%</span>
                </div>
                <Progress value={importProgress} className="h-2 bg-muted" />
              </div>
            )}

            {importStatus && (
              <Alert className={`border ${importStatus.includes('Error') ? 'bg-destructive/10 border-destructive/20 text-destructive' : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-600'}`}>
                <AlertDescription className="text-xs font-medium">
                  {importStatus}
                </AlertDescription>
              </Alert>
            )}

            <div className="relative">
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleFileSelect}
                className="hidden"
                id="backup-upload"
                disabled={isImporting}
              />
              <label htmlFor="backup-upload">
                <Button
                  type="button"
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white h-12 text-base shadow-sm cursor-pointer"
                  disabled={isImporting}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {isImporting ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      <FileJson className="mr-2 h-4 w-4" />
                      Seleccionar Archivo JSON
                    </>
                  )}
                </Button>
              </label>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
